/**
 * The card's two skins: the product's `--g-*` tokens, and the imported
 * design's `--gd-*` equivalents inside the ≥1200px light-theme frame
 * (`useAssistantDesign`, the gate MessageInput uses for its own design branch).
 *
 * LITERAL class strings only: Tailwind 4 generates an arbitrary-value utility
 * from a literal it finds in source, so a class assembled at runtime would not
 * exist. Structure (sizes, spacing, layout) lives in the components; this file
 * holds the token-bearing classes and the two surfaces' shapes.
 *
 * Text tiers: `--g-ink-2` is the faintest text (theme.css "INK TIERS"); inside
 * the design frame descriptions use `--gd-ink-2`, which holds 4.5:1 over the
 * selected wash where `--gd-ink-3` does not (design-world.css header).
 */
export interface ClarifySkin {
  /** The strip that stands where the composer was. */
  slot: string;
  /** The card surface: radius, border, fill, shadow, height cap. */
  card: string;
  iconButton: string;
  chip: string;
  legend: string;
  row: string;
  rowOn: string;
  ring: string;
  desc: string;
  descOn: string;
  mark: string;
  markOn: string;
  footer: string;
  skipButton: string;
  primaryButton: string;
  customField: string;
  customInput: string;
  confirmButton: string;
  counter: string;
}

const APP: ClarifySkin = {
  slot: "pt-2 md:px-4 md:pb-3",
  card:
    "mx-auto flex max-h-[70dvh] w-full max-w-[720px] flex-col overflow-hidden rounded-t-[var(--g-radius-lg)] border-t border-[var(--g-rule)] bg-[var(--g-elevated)] pb-[env(safe-area-inset-bottom)] shadow-[var(--g-shadow-lg)] md:rounded-[var(--g-radius-lg)] md:border md:pb-0",
  iconButton:
    "grid size-11 shrink-0 place-items-center rounded-[var(--g-radius-sm)] text-[var(--g-ink-2)] transition-colors duration-150 hover:bg-[var(--g-primary-soft)] hover:text-primary disabled:pointer-events-none disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] motion-reduce:transition-none",
  chip: "min-w-0 flex-1 truncate px-1 font-mono text-[12px] font-semibold tracking-[0.08em] text-[var(--g-ink-2)]",
  legend: "mb-3 text-[17px] font-semibold leading-snug text-foreground",
  row: "border-[var(--g-rule)] bg-[var(--g-surface)] text-foreground hover:border-[var(--g-rule-strong)]",
  rowOn: "border-[var(--g-primary)] bg-[var(--g-primary-soft)] text-[var(--g-primary-ink)]",
  ring: "focus-visible:ring-[var(--g-primary)] focus-visible:ring-offset-[var(--g-elevated)]",
  desc: "text-[var(--g-ink-2)]",
  descOn: "text-[var(--g-primary-ink)]",
  mark: "border-[var(--g-rule-strong)] bg-transparent",
  markOn: "border-[var(--g-primary)] bg-[var(--g-primary)] text-[var(--g-on-primary)]",
  footer: "flex shrink-0 items-center justify-between gap-3 border-t border-[var(--g-hairline)] px-2 py-2 md:px-3",
  skipButton:
    "min-h-11 rounded-[var(--g-radius-sm)] px-3 text-left text-[15px] font-medium text-[var(--g-ink-2)] transition-colors hover:text-foreground disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]",
  primaryButton:
    "inline-flex min-h-11 shrink-0 items-center rounded-[var(--g-radius-sm)] bg-primary px-5 text-[15px] font-semibold text-primary-foreground transition-[filter] hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--g-elevated)]",
  customField:
    "flex items-end gap-2 rounded-[var(--g-radius-sm)] border border-[var(--g-rule)] bg-[var(--g-surface)] py-1 pl-3 pr-1 focus-within:border-[var(--g-primary)] focus-within:shadow-[0_0_0_3px_var(--g-primary-soft)]",
  customInput:
    "block max-h-[120px] min-h-[44px] w-full flex-1 resize-none bg-transparent py-[11px] text-[16px] leading-[1.45] text-foreground outline-none placeholder:text-[var(--g-ink-2)]",
  confirmButton:
    "grid size-11 shrink-0 place-items-center rounded-[var(--g-radius-sm)] bg-primary text-[var(--g-on-primary)] disabled:bg-[var(--g-sunken)] disabled:text-[var(--g-ink-3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]",
  counter: "mt-1 text-right text-[13px] text-[var(--g-ink-2)]",
};

const DESIGN: ClarifySkin = {
  slot: "px-8 pb-5 pt-3",
  card:
    "mx-auto flex max-h-[70dvh] w-full max-w-[780px] flex-col overflow-hidden rounded-[var(--g-radius-lg)] border border-[var(--gd-rule-2)] bg-[var(--gd-surface)] shadow-[var(--g-shadow-lg)]",
  iconButton:
    "grid size-11 shrink-0 place-items-center rounded-[var(--g-radius-sm)] text-[var(--gd-ink-2)] transition-colors duration-150 hover:bg-[var(--gd-wash)] hover:text-[var(--gd-primary)] disabled:pointer-events-none disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gd-primary)] motion-reduce:transition-none",
  chip: "min-w-0 flex-1 truncate px-1 font-mono text-[12px] font-semibold tracking-[0.08em] text-[var(--gd-ink-2)]",
  legend: "mb-3 text-[17px] font-semibold leading-snug text-[var(--gd-ink)]",
  row: "border-[var(--gd-rule-2)] bg-[var(--gd-surface)] text-[var(--gd-ink-body)] hover:border-[var(--gd-field-border)]",
  rowOn: "border-[var(--gd-primary)] bg-[var(--gd-wash-2)] text-[var(--gd-ink)]",
  ring: "focus-visible:ring-[var(--gd-primary)] focus-visible:ring-offset-[var(--gd-surface)]",
  desc: "text-[var(--gd-ink-2)]",
  descOn: "text-[var(--gd-ink-2)]",
  mark: "border-[var(--gd-field-border)] bg-transparent",
  markOn: "border-[var(--gd-primary)] bg-[var(--gd-primary)] text-[var(--gd-on-primary)]",
  footer: "flex shrink-0 items-center justify-between gap-3 border-t border-[var(--gd-rule-4)] px-3 py-2",
  skipButton:
    "min-h-11 rounded-[var(--g-radius-sm)] px-3 text-left text-[15px] font-medium text-[var(--gd-ink-2)] transition-colors hover:text-[var(--gd-ink)] disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gd-primary)]",
  primaryButton:
    "inline-flex min-h-11 shrink-0 items-center rounded-[var(--g-radius-sm)] bg-[var(--gd-primary)] px-5 text-[15px] font-semibold text-[var(--gd-on-primary)] transition-colors hover:bg-[var(--gd-primary-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gd-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--gd-surface)]",
  customField:
    "flex items-end gap-2 rounded-[var(--g-radius-sm)] border border-[var(--gd-field-border)] bg-[var(--gd-surface)] py-1 pl-3 pr-1 focus-within:border-[var(--gd-primary)] focus-within:shadow-[0_0_0_3px_var(--gd-focus-ring-soft)]",
  customInput:
    "block max-h-[120px] min-h-[44px] w-full flex-1 resize-none bg-transparent py-[11px] text-[16px] leading-[1.45] text-[var(--gd-ink)] outline-none placeholder:text-[var(--gd-ink-3)]",
  confirmButton:
    "grid size-11 shrink-0 place-items-center rounded-[var(--g-radius-sm)] bg-[var(--gd-primary)] text-[var(--gd-on-primary)] disabled:bg-[var(--gd-wash-2)] disabled:text-[var(--gd-ink-3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gd-primary)]",
  counter: "mt-1 text-right text-[13px] text-[var(--gd-ink-2)]",
};

export function clarifySkin(design: boolean): ClarifySkin {
  return design ? DESIGN : APP;
}
